import { useEffect, useState } from 'react'

export type BreadcrumbInitialData = { title: string; path?: string, id: string }
type BreadcrumbWith = { id: string, width: number }

// Function to retrieve the width of each HTML breadcrumb element
const getBreadcrumbWidths = (breadcrumbs: BreadcrumbInitialData[]): BreadcrumbWith[] | null => {
  const arrOfWidths: any[] = []
  for (let i = 0; i < breadcrumbs.length; i++) {
    const id = breadcrumbs[i].id
    const element = document.querySelector(`#${id}`) as HTMLElement
    arrOfWidths.push({ id, width: element?.getBoundingClientRect().width })
  }
  // if any of the widths is 0, it means that the element is not rendered yet
  // return null to not interfere with the visibility calculation
  if (arrOfWidths.some(el => !el.width)) {
    return null
  }
  return arrOfWidths
}

export const useBreadcrumbsVisibility = (breadcrumbs: BreadcrumbInitialData[], containerRef: React.RefObject<HTMLDivElement | null>) => {

  const [containerWidth, setContainerWidth] = useState(0)

  const [showEllipsis, setShowEllipsis] = useState(false)
  const [visibilityData, setVisibilityData] = useState<{ id: string, hidden: boolean }[]>([])
  const [breadcrumbWidths, setBreadcrumbWidths] = useState<BreadcrumbWith[]>([])
  const [firstAndLastExceedSpace, setFirstAndLastExceedSpace] = useState(false)

  useEffect(() => {
    const handleResize = (entries: ResizeObserverEntry[]) => {
      if (entries[0]) {
        const { width: newWidth } = entries[0].contentRect

        if (containerWidth !== 0) {
          const breadcrumbsWidths = getBreadcrumbWidths(breadcrumbs)
          if (breadcrumbsWidths) {
            setBreadcrumbWidths(breadcrumbsWidths)
          }
        }
        setContainerWidth(newWidth)

      }
    }

    // ResizeObserver is necessary to get the width of the container
    // window resize event is not enough because of rendering issues
    // sometimes width is 0
    const resizeObserver = new ResizeObserver(handleResize)

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current)
      }
    }
  }, [])

  // Always render the first and last breadcrumb. The remaining breadcrumbs are counted from the end.
  const getReversedBreadcrumbWidths = (widths: BreadcrumbWith[]) => {
    let breadcrumbWidthsCopy = [...widths]
    if (breadcrumbWidthsCopy.length === 0) {
      // Verify if the widths have been calculated; if not, retry the calculation.
      breadcrumbWidthsCopy = getBreadcrumbWidths(breadcrumbs) || []
      setBreadcrumbWidths(breadcrumbWidthsCopy)
    }
    return [...breadcrumbWidthsCopy].reverse()
  }

  useEffect(() => {
    const updateBreadcrumbs = () => {
      if (containerRef.current && containerWidth) {

        let showEllipsis = false

        const reversedBreadcrumbWidths = getReversedBreadcrumbWidths(breadcrumbWidths)
        if (reversedBreadcrumbWidths.length === 0) {
          return
        }

        // First and last item are always visible
        const firstBreadcrumb = reversedBreadcrumbWidths.pop()!
        const lastBreadcrumb = reversedBreadcrumbWidths.shift()!

        let totalWidth = firstBreadcrumb?.width + lastBreadcrumb?.width

        const safetyMargin = 40
        if (totalWidth + safetyMargin > containerWidth) {
          setFirstAndLastExceedSpace(true)
        } else {
          setFirstAndLastExceedSpace(false)
        }

        const preparedVisibleData = reversedBreadcrumbWidths.map((breadcrumb) => {

          if (totalWidth + breadcrumb.width + safetyMargin < containerWidth) {
            totalWidth += breadcrumb.width
            return { id: breadcrumb.id, hidden: false }
          }
          totalWidth = containerWidth
          showEllipsis = true
          return { id: breadcrumb.id, hidden: true }
        })

        setVisibilityData([{ id: firstBreadcrumb?.id, hidden: false }, ...preparedVisibleData, { id: lastBreadcrumb?.id, hidden: false }])
        setShowEllipsis(showEllipsis)

      }

    }

    updateBreadcrumbs()

  }, [breadcrumbs, containerWidth])

  return { showEllipsis, visibilityData, firstAndLastExceedSpace }
}